import { useHistory, useLocation } from 'react-router-dom'
import CartList from './components/CartList'
import Checkout from './components/Checkout'
import Documents from './components/Documents'
import Payment from './components/Payment'
import ProductList from './components/ProductList'
import SendDocuments, { SendDocumentsStep } from './components/SendDocuments'
import { clearOrderId, getOrderId } from './utils/orderStorage'

/** Subpáginas do fluxo de documentos pós-pagamento */
const DOCUMENT_STEP_BY_SUBPAGE: Record<string, SendDocumentsStep> = {
  'documentos-receita': 'receita',
  'documentos-pessoais': 'pessoais',
  'documentos-laudo': 'laudo',
  'documentos-anvisa': 'anvisa'
}

const SUBPAGE_BY_DOCUMENT_STEP: Record<SendDocumentsStep, string> = {
  receita: 'documentos-receita',
  pessoais: 'documentos-pessoais',
  laudo: 'documentos-laudo',
  anvisa: 'documentos-anvisa'
}

export default function Cart() {
  const history = useHistory()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const subpage = queryParams.get('subpagina') || 'carrinho'
  const orderIdFromUrl = queryParams.get('order_id')

  const navigateToProducts = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'produtos')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const navigateToCheckout = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'checkout')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const navigateToPayment = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'pagamento')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  /**
   * Entrada do fluxo de documentos após o pagamento.
   * O fluxo antigo (subpagina=documentos) segue acessível por URL direta,
   * mas não é mais o destino do pós-pagamento.
   */
  const navigateToSendDocuments = (paidOrderId?: number) => {
    const orderId = paidOrderId || orderIdFromUrl || getOrderId()
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', SUBPAGE_BY_DOCUMENT_STEP.receita)
    if (orderId) {
      newParams.set('order_id', orderId.toString())
    }
    clearOrderId()
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const navigateToDocumentStep = (step: SendDocumentsStep) => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', SUBPAGE_BY_DOCUMENT_STEP[step])
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  /** Mesma página de detalhe usada pela listagem de pedidos da associação */
  const navigateToOrderDetails = (orderId: number | string) => {
    history.push(`/AreaAssociados/detalhes-do-pedido/${orderId}`)
  }

  const navigateBackToCheckout = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'checkout')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const navigateBackToPayment = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'pagamento')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const navigateToOrders = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'pedidos-todos')
    newParams.delete('subpagina')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const handleProductAdded = () => {
    // Optional: Show toast notification or navigate back to cart
  }

  const navigateToCart = () => {
    const newParams = new URLSearchParams(location.search)
    newParams.set('pagina', 'carrinho')
    newParams.set('subpagina', 'carrinho')
    history.push(`${location.pathname}?${newParams.toString()}`)
  }

  const handleCheckoutComplete = () => {
    // Navigate to payment page
    navigateToPayment()
  }

  const handlePaymentComplete = (paidOrderId?: number) => {
    // Após o pagamento o usuário envia os documentos exigidos pelo tipo de produto
    navigateToSendDocuments(paidOrderId)
  }

  const handleDocumentsComplete = () => {
    // Navigate to orders page after documents submission
    navigateToOrders()
  }

  if (subpage === 'produtos') {
    return <ProductList onProductAdded={handleProductAdded} onNavigateToCart={navigateToCart} />
  }

  if (subpage === 'checkout') {
    return <Checkout onCheckoutComplete={handleCheckoutComplete} />
  }

  if (subpage === 'pagamento') {
    return <Payment onPaymentComplete={handlePaymentComplete} onBack={navigateBackToCheckout} />
  }

  if (subpage === 'documentos') {
    // Tenta pegar o order_id da URL, se não existir, tenta do localStorage como fallback
    const orderIdFromStorage = getOrderId()
    const orderId = orderIdFromUrl ? Number(orderIdFromUrl) : orderIdFromStorage
    return <Documents orderId={orderId} onComplete={handleDocumentsComplete} onBack={navigateBackToPayment} />
  }

  const documentStep = DOCUMENT_STEP_BY_SUBPAGE[subpage]
  if (documentStep) {
    const orderId = orderIdFromUrl ? Number(orderIdFromUrl) : getOrderId()
    return (
      <SendDocuments
        orderId={orderId}
        step={documentStep}
        onNavigateToStep={navigateToDocumentStep}
        onComplete={() => (orderId ? navigateToOrderDetails(orderId) : navigateToOrders())}
        onBack={navigateToOrders}
      />
    )
  }

  return <CartList onNavigateToProducts={navigateToProducts} onNavigateToCheckout={navigateToCheckout} />
}
